import { useCallback, useMemo } from 'react'
import { useAdminI18n } from 'i18n/feature'
import { buildChampionsSummaryCards, getChampionsOperatorOptions } from 'champion/championsTableConfig'
import { operatorTypes } from 'core/table/interfaces/Filters'

const STATUS_FILTER_COLUMNS = new Set(['status', 'expectedContact'])

export function useChampionsPageI18n(valuePerHour: number) {
  const { t } = useAdminI18n('admin')

  const cards = useMemo(() => buildChampionsSummaryCards(valuePerHour, t), [valuePerHour, t])

  const labels = useMemo(
    () => ({
      title: t('pages.champions.title', { defaultValue: 'Champions' }),
      subtitle: t('pages.champions.subtitle', {
        defaultValue: "Add and manage your organization's volunteers",
      }),
      searchPlaceholder: t('pages.champions.searchPlaceholder', { defaultValue: 'Search Champions' }),
      filterSubHeader: t('pages.champions.filterSubHeader', {
        defaultValue: 'Select volunteer attributes to filter by:',
      }),
      message: t('table.actions.message', { defaultValue: 'Message' }),
      export: t('table.actions.export', { defaultValue: 'Export' }),
      addChampion: t('pages.champions.addChampion', { defaultValue: 'Add Champion' }),
      resendVerification: t('pages.champions.resendVerification', { defaultValue: 'Resend Verification Link' }),
      addToChat: t('pages.champions.addToChat', { defaultValue: 'Add to Chat' }),
      addToGroup: t('pages.champions.addToGroup', { defaultValue: 'Add to Group' }),
      addRemoveTags: t('pages.champions.addRemoveTags', { defaultValue: 'Add/Remove Tags' }),
      removeChampion: t('pages.champions.removeChampion', { defaultValue: 'Remove Champion' }),
      removeChampions: t('pages.champions.removeChampions', { defaultValue: 'Remove Champions' }),
      importVolunteers: t('pages.champions.importVolunteers', { defaultValue: 'Import Volunteers' }),
      showAllChampions: t('pages.champions.showAllChampions', { defaultValue: 'Show All Champions' }),
      loadingChampions: t('pages.champions.loadingChampions', { defaultValue: 'Loading Champions…' }),
      allVolunteers: t('pages.champions.allVolunteers', { defaultValue: 'All Volunteers' }),
      exportTo: t('pages.champions.exportTo', { defaultValue: 'Export to...' }),
      littleGreenLight: t('pages.champions.integrations.littleGreenLight', { defaultValue: 'Little Green Light' }),
      donorPerfect: t('pages.champions.integrations.donorPerfect', { defaultValue: 'DonorPerfect' }),
      fastn: t('pages.champions.integrations.fastn', { defaultValue: 'Fastn' }),
    }),
    [t],
  )

  const getOperatorOptions = useCallback(
    (column: string) => {
      const options = getChampionsOperatorOptions(column)
      if (!STATUS_FILTER_COLUMNS.has(column)) {
        return options
      }
      return options.map(option => {
        if (option.value === operatorTypes.ANY_OF) {
          return {
            ...option,
            displayValue: t('table.filters.includes', { defaultValue: 'Includes' }),
          }
        }
        if (option.value === operatorTypes.NOT_ANY_OF) {
          return {
            ...option,
            displayValue: t('table.filters.doesNotInclude', { defaultValue: 'Does not include' }),
          }
        }
        return option
      })
    },
    [t],
  )

  return { labels, cards, getOperatorOptions }
}
